import type { FC } from "react"
import React from "react"
import type { StackProps } from "@chakra-ui/react"
import { Skeleton } from "@chakra-ui/react"
import invariant from "tiny-invariant"

import type { VoteStats } from "query/graphql"
import { ProposalStatus, useProposalVotesCastQuery } from "query/graphql"
import CardContainer from "common/components/CardContainer"
import VotesCastResults, {
  VotesCastResultStatus,
} from "common/components/voting/VotesCastResults"
import { useDevice } from "common/hooks/useDevice"
import VotesCast from "common/components/voting/VotesCast"
import { ROUTES } from "common/constants/routes"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useProposal } from "proposal/providers/ProposalProvider"
import { useVotesCastResultsCalculations } from "common/hooks/useVotesCastResultsCalculations"

type Props = {
  proposalId: string
  slug: string
}

const ProposalVotesCast: FC<Props & StackProps> = ({
  proposalId,
  slug,
  ...stackProps
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const { organization } = useOrganization()
  const { isIncludePendingVotes } = useProposal()

  const { data, isLoading } = useProposalVotesCastQuery({
    input: {
      id: proposalId,
    },
  })

  const { isQuorumMet, totalWeightLabel, quorumLabel, isMajoritySupport } =
    useVotesCastResultsCalculations({
      quorum: data?.proposal.quorum ?? "0",
      decimals: data?.proposal.governor?.token.decimals ?? 18,
      voteStats: data?.proposal.voteStats,
      isIncludePendingVotes,
      governorType: data?.proposal?.governor?.type,
    })

  if (isLoading) {
    return (
      <CardContainer isTableView title="Votes">
        <Skeleton as="section" h="30.0875rem" mt={4} mx={2} {...stackProps} />{" "}
      </CardContainer>
    )
  }

  if (!data || !data?.proposal) return null

  const { proposal: proposal } = data
  const { governor, voteStats } = proposal

  invariant(governor, "governor is required")

  const { token, id: govId } = governor
  const { decimals } = token
  const isProposalActive =
    proposal.status === ProposalStatus.Active ||
    proposal.status === ProposalStatus.Extended
  const isProposalPending = proposal.status === ProposalStatus.Pending

  return (
    <>
      {!isProposalPending ? (
        <CardContainer
          isTableView
          display={onLargeDevice}
          pb={2}
          title={isProposalActive ? "Current Votes" : "Final Votes"}
        >
          <VotesCastResults
            decimals={decimals}
            isIncludePendingVotes={isIncludePendingVotes}
            voteStats={voteStats as VoteStats[]}
            votesCastResultStatuses={
              <>
                <VotesCastResultStatus
                  isSuccess={isQuorumMet}
                  label="Quorum"
                  value={`${totalWeightLabel} of ${quorumLabel}`}
                />
                <VotesCastResultStatus
                  isSuccess={isMajoritySupport}
                  label="Majority support"
                  value={isMajoritySupport ? "Yes" : "No"}
                />
              </>
            }
          />
        </CardContainer>
      ) : null}
      <CardContainer
        isTableView
        display={onLittleDevice}
        pb={2}
        title={isProposalActive ? "Current Votes" : "Final Votes"}
      >
        <VotesCast
          decimals={decimals}
          generateVoterLink={(address) =>
            ROUTES.governance.delegate.profile(slug, address)
          }
          proposalId={proposalId}
          viewAllVotesLink={ROUTES.governance.proposal.votes(
            slug,
            proposalId,
            organization && organization?.governorIds.length > 1 ? govId : "",
          )}
          voteStats={voteStats as VoteStats[]}
        />
      </CardContainer>
    </>
  )
}

export default ProposalVotesCast
